import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, X, Check, Loader2, Search, Hand } from 'lucide-react';
import { api } from '@/api/client';

/**
 * Attendance as a MONTH CALENDAR, and marking a day from it.
 *
 * The monthly grid is one row per employee and one narrow column per day — fine for reading a
 * person's month, hopeless for "what happened on the 14th", which is how attendance is actually
 * marked. This shows the month as a calendar, each date carrying its own present/absent tally, and
 * opens the whole staff list for that date when you click it.
 *
 * It reuses the SAME endpoints as the grid — `GET /hr/attendance` for the month and
 * `POST /hr/attendance/mark` for the change — so the two views can never disagree, and a day marked
 * here is a manual row exactly as it is from the grid.
 */

export const STATUSES = [
  { code: 'P', label: 'Present', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { code: 'A', label: 'Absent', cls: 'bg-rose-100 text-rose-700 border-rose-300' },
  { code: 'HD', label: 'Half day', cls: 'bg-teal-100 text-teal-700 border-teal-300' },
  { code: 'L', label: 'Leave', cls: 'bg-violet-100 text-violet-700 border-violet-300' },
  { code: 'H', label: 'Holiday', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  { code: 'WO', label: 'Weekly off', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  { code: 'WF', label: 'Work from home', cls: 'bg-sky-100 text-sky-700 border-sky-300' },
];

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const statusOf = (code?: string | null) => STATUSES.find((s) => s.code === code);

/** `2026-09-04` for day 4 — built by hand, because `toISOString()` would shift it a day in IST. */
const ymd = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

export function AttendanceCalendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [openDay, setOpenDay] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'att', year, month],
    queryFn: async () => (await api.get('/hr/attendance', { params: { year, month } })).data,
  });

  /** day-of-month → what every employee has on it, plus the tallies the tile shows. */
  const byDay = useMemo(() => {
    const out: Record<number, { present: number; absent: number; other: number; manual: number; marked: number }> = {};
    for (const e of data?.employees ?? []) {
      for (const d of e.days ?? []) {
        const day = Number(String(d.attDate).slice(8, 10));
        const t = (out[day] ??= { present: 0, absent: 0, other: 0, manual: 0, marked: 0 });
        t.marked++;
        if (d.status === 'P' || d.status === 'HD' || d.status === 'WF') t.present++;
        else if (d.status === 'A') t.absent++;
        else t.other++;
        if (d.source === 'MANUAL') t.manual++;
      }
    }
    return out;
  }, [data]);

  const daysInMonth = data?.days ?? new Date(year, month, 0).getDate();
  // Monday-first, which is how the office reads a week.
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const step = (by: number) => {
    const d = new Date(year, month - 1 + by, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };
  const isToday = (d: number) => year === today.getFullYear() && month === today.getMonth() + 1 && d === today.getDate();
  // Attendance records a day that has happened. A future date is shown, greyed, and does nothing —
  // the server refuses it too, so this is the courtesy and that is the rule.
  const isFuture = (d: number) => new Date(year, month - 1, d).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button onClick={() => step(-1)} className="p-1.5 border border-slate-300 rounded-lg hover:bg-slate-50" title="Previous month">
          <ChevronLeft size={16} />
        </button>
        <div className="font-semibold text-slate-800 min-w-[9.5rem] text-center">{MONTHS[month - 1]} {year}</div>
        <button onClick={() => step(1)} className="p-1.5 border border-slate-300 rounded-lg hover:bg-slate-50" title="Next month">
          <ChevronRight size={16} />
        </button>
        <button onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); }}
          className="px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs hover:bg-slate-50">This month</button>
        <div className="ml-auto text-xs text-slate-500 flex items-center gap-1.5">
          <Hand size={12} className="text-indigo-600" /> the hand means someone marked that day by hand
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1.5 text-center text-[11px] font-medium text-slate-500 mb-1.5">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {Array.from({ length: firstWeekday }, (_, i) => <div key={`pad${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => {
              const t = byDay[d];
              const sunday = new Date(year, month - 1, d).getDay() === 0;
              const future = isFuture(d);
              return (
                <button key={d} onClick={() => { if (!future) setOpenDay(d); }} disabled={future}
                  title={future ? 'This day has not happened yet' : undefined}
                  className={`relative border rounded-lg p-2 text-left min-h-[4.6rem] transition
                    ${future ? 'opacity-40 cursor-not-allowed' : 'hover:border-primary hover:shadow-sm'}
                    ${isToday(d) ? 'border-primary ring-1 ring-primary/30' : 'border-slate-200'}
                    ${sunday ? 'bg-slate-50' : 'bg-white'}`}>
                  <div className="flex items-start justify-between">
                    <span className={`text-sm font-semibold ${isToday(d) ? 'text-primary' : 'text-slate-700'}`}>{d}</span>
                    {!!t?.manual && <Hand size={11} className="text-indigo-600 mt-0.5" aria-label={`${t.manual} marked by hand`} />}
                  </div>
                  {t ? (
                    <div className="mt-1 space-y-0.5">
                      {!!t.present && <div className="text-[10px] text-emerald-700">{t.present} present</div>}
                      {!!t.absent && <div className="text-[10px] text-rose-700">{t.absent} absent</div>}
                      {!!t.other && <div className="text-[10px] text-slate-500">{t.other} leave / off</div>}
                    </div>
                  ) : (
                    <div className="mt-1 text-[10px] text-slate-300">{future ? '—' : 'not marked'}</div>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}

      {openDay !== null && data && (
        <DaySheet year={year} month={month} day={openDay} employees={data.employees} onClose={() => setOpenDay(null)} />
      )}
    </div>
  );
}

/**
 * One date, every employee, mark them off.
 *
 * Nothing is sent until Save: the sheet holds the changes so a whole day can be marked in one go
 * rather than one request per person. Only what CHANGED is sent, so opening a day and closing it
 * again cannot quietly rewrite rows — including turning somebody's self-marked row into a manual one.
 */
function DaySheet({ year, month, day, employees, onClose }: {
  year: number; month: number; day: number; employees: any[]; onClose: () => void;
}) {
  const qc = useQueryClient();
  const date = ymd(year, month, day);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<Record<number, string>>({});

  /** What is already on file for this date, per employee id. */
  const existing = useMemo(() => {
    const out: Record<number, any> = {};
    for (const e of employees) {
      const hit = (e.days ?? []).find((d: any) => String(d.attDate).slice(0, 10) === date);
      if (hit) out[e.id] = hit;
    }
    return out;
  }, [employees, date]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e: any) =>
      String(e.employeeName ?? '').toLowerCase().includes(q) ||
      String(e.employeeId ?? '').toLowerCase().includes(q) ||
      String(e.designation ?? '').toLowerCase().includes(q));
  }, [employees, search]);

  const changed = Object.entries(draft).filter(([id, code]) => (existing[Number(id)]?.status ?? '') !== code);

  const save = useMutation({
    mutationFn: async () => {
      const rows = changed.map(([id, status]) => ({ employeeId: Number(id), attDate: date, status }));
      return (await api.post('/hr/attendance/mark', { rows })).data;
    },
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['hr', 'att'] });
      toast.success(`Saved ${r?.upserted ?? changed.length} of ${employees.length}`);
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save'),
  });

  /** Fill everyone currently listed — the common case is "everybody was in today". */
  const fillAll = (code: string) => {
    const next = { ...draft };
    for (const e of shown) next[e.id] = code;
    setDraft(next);
  };

  const pretty = new Date(year, month - 1, day).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-800">{pretty}</h3>
            <p className="text-xs text-slate-500">Pick a status for anyone you want to change. Nothing is saved until you press Save.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a person…"
              className="pl-7 pr-2 py-1.5 border border-slate-300 rounded-lg text-sm w-56" />
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            Mark everyone shown:
            {STATUSES.slice(0, 3).map((s) => (
              <button key={s.code} onClick={() => fillAll(s.code)}
                className={`px-2 py-1 rounded border text-[11px] ${s.cls}`}>{s.label}</button>
            ))}
          </div>
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-xs text-slate-600">
                <th className="px-4 py-2">Employee</th>
                <th className="px-4 py-2">On file</th>
                <th className="px-4 py-2">Set to</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((e: any) => {
                const cur = existing[e.id];
                const picked = draft[e.id] ?? cur?.status ?? '';
                return (
                  <tr key={e.id} className="border-t border-slate-100">
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-800">{e.employeeName}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{e.employeeId}{e.designation ? ` · ${e.designation}` : ''}</div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {cur ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`px-1.5 py-0.5 rounded border text-[11px] ${statusOf(cur.status)?.cls ?? 'bg-slate-100 text-slate-600 border-slate-300'}`}>
                            {statusOf(cur.status)?.label ?? cur.status}
                          </span>
                          {cur.source === 'MANUAL'
                            ? <Hand size={11} className="text-indigo-600" aria-label="Marked by hand" />
                            : <span className="text-[10px] text-slate-400">selfie</span>}
                        </span>
                      ) : <span className="text-xs text-slate-300">not marked</span>}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {STATUSES.map((s) => (
                          <button key={s.code} onClick={() => setDraft({ ...draft, [e.id]: s.code })}
                            title={s.label}
                            className={`px-1.5 py-0.5 rounded border text-[11px] ${picked === s.code ? s.cls + ' font-semibold' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>
                            {s.code}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!shown.length && <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400">Nobody matches that.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
          <div className="text-xs text-slate-500">
            {changed.length ? `${changed.length} change${changed.length === 1 ? '' : 's'} ready to save` : 'No changes yet'}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
            <button onClick={() => save.mutate()} disabled={!changed.length || save.isPending}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
              {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Save {changed.length ? `(${changed.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
